import { Directive, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CountupOptions } from './countup.interface';

@Directive({
    selector: '[finderCountup]',
    standalone: true,
})
export class CountupDirective implements OnInit {
    @Output() countupComplete = new EventEmitter();

    private animationDuration = 1500; // Default 1.5 seconds
    private frameDuration = 1000 / 60; // 60 fps
    private totalFrames: number;
    private countTo: number;
    private destroy$ = new Subject<void>();

    @Input()
    set finderCountup(options: CountupOptions) {
        this.animationDuration = options.animationDuration || this.animationDuration;
        this.countTo = options.value;
    }

    constructor(
        private elementRef: ElementRef,
    ) {}

    ngOnInit() {
        this.totalFrames = Math.round(this.animationDuration / this.frameDuration);
        this.animate();
    }

    // An ease-out function that slows the count as it progresses
    private easeOutQuad = (t: number) => t * (2 - t);

    // Based on https://codepen.io/jshakes/pen/KKpjdYv
    private animate() {
        const el = this.elementRef.nativeElement;
        let frame = 0;

        // Start the animation running 60 times per second
        interval(this.frameDuration)
            .pipe(
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                frame++;
                // Calculate our progress as a value between 0 and 1
                // Pass that value to our easing function to get our
                // progress on a curve
                const progress = this.easeOutQuad(frame / this.totalFrames);

                // Use the progress value to calculate the current count
                const currentCount = Math.round(this.countTo * progress);

                // If the current count has changed, update the element
                if (parseInt(el.innerHTML, 10) !== currentCount) {
                    el.innerHTML = currentCount;
                }

                // If we’ve reached our last frame, stop the animation
                if (frame === this.totalFrames) {
                    this.destroy$.next();
                    this.destroy$.complete();
                    this.countupComplete.emit('true');
                }
            });
    }
}
